// src/components/Topics.jsx
import React, { useState, useRef, useEffect } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useParams } from 'react-router-dom';
import './Topics.css';

function Topics({ defaultTopic, isWidget = false, parentInfo = null }) {
  const { abbreviation } = useParams();

  const [topicsData, setTopicsData] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [errorTopics, setErrorTopics] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    async function fetchTopics() {
      setLoadingTopics(true);
      setErrorTopics(null);

      // Widget mode: pull via secured proxy (districtData)
      if (isWidget) {
        try {
          const res = await fetch('/.netlify/functions/districtData', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              resource: 'topics',
              abbreviation,
              parentOrigin: parentInfo?.origin,
              parentUrl: parentInfo?.url,
            }),
          });

          if (res.status === 403) {
            setErrorTopics({ message: 'Not authorized' });
            setLoadingTopics(false);
            return;
          }

          if (!res.ok) {
            const payload = await res.json().catch(() => ({}));
            setErrorTopics({ message: payload.error || 'Failed to load topics' });
            setLoadingTopics(false);
            return;
          }

          const payload = await res.json();
          const data = payload.topics || [];

          const parsed = (data || []).map((topic) => ({
            id: topic.id,
            title: topic.title,
            content: topic.description || '',
          }));

          setTopicsData(parsed);
          setLoadingTopics(false);
          return;
        } catch (e) {
          setErrorTopics({ message: e.message || 'Failed to load topics' });
          setLoadingTopics(false);
          return;
        }
      }

      // Non-widget: existing behavior
      const { data: district, error: distErr } = await supabase
        .from('Districts')
        .select('id')
        .eq('abbreviation', abbreviation)
        .single();

      if (distErr || !district) {
        setErrorTopics(distErr || { message: 'District not found' });
        setLoadingTopics(false);
        return;
      }

      const { data, error } = await supabase
        .from('Topics')
        .select('*')
        .eq('districtId', district.id)
        .eq('current', true)
        .order('id', { ascending: false });

      if (error) {
        setErrorTopics(error);
      } else {
        const parsed = data.map((topic) => ({
          id: topic.id,
          title: topic.title,
          content: topic.description || '',
        }));
        setTopicsData(parsed);
      }

      setLoadingTopics(false);
    }

    fetchTopics();
  }, [abbreviation, isWidget, parentInfo]);

  // Widget link handling
  useEffect(() => {
    if (isWidget && topicsData.length > 0 && contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    }
  }, [isWidget, topicsData, selectedIndex]);

  useEffect(() => {
    if (defaultTopic && topicsData.length > 0) {
      const idx = topicsData.findIndex((t) => t.title.toLowerCase() === defaultTopic.toLowerCase());
      if (idx !== -1) setSelectedIndex(idx);
    }
  }, [defaultTopic, topicsData]);

  useEffect(() => {
    if (topicsData.length > 0 && window.location.hash) {
      const hash = window.location.hash.substring(1);
      const idx = topicsData.findIndex((t) => `topic-${t.id}` === hash);
      if (idx !== -1) {
        setSelectedIndex(idx);
        document.getElementById(hash)?.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
        });
      }
    }
  }, [topicsData]);

  const handleLeftArrowClick = () => scrollContainerRef.current?.scrollBy({ left: -120, behavior: 'smooth' });
  const handleRightArrowClick = () => scrollContainerRef.current?.scrollBy({ left: 120, behavior: 'smooth' });

  if (loadingTopics) return <p>Loading topics...</p>;
  if (errorTopics) return <p>Error loading topics: {errorTopics.message}</p>;
  if (topicsData.length === 0) return <p>No topics found.</p>;

  const currentTopic = topicsData[selectedIndex];

  return (
    <>
      <div className="topics-wrapper">
        <div className="topics-bar">
          <button className="arrow left" onClick={handleLeftArrowClick}>
            &lsaquo;
          </button>
          <div className="topics-scroll-container" ref={scrollContainerRef}>
            {topicsData.map((t, i) => (
              <div
                id={`topic-${t.id}`}
                key={t.id}
                className={`topic-item ${i === selectedIndex ? 'active' : ''}`}
                onClick={() => setSelectedIndex(i)}
              >
                {t.title}
              </div>
            ))}
          </div>
          <button className="arrow right" onClick={handleRightArrowClick}>
            &rsaquo;
          </button>
        </div>

        <div className="topic-content-container">
          <div className="topic-header-row">
            <h2>{currentTopic.title}</h2>
            <div className="topic-header-actions">
              {/* Actions removed */}
            </div>
          </div>

          <div
            ref={contentRef}
            className="topic-details"
            dangerouslySetInnerHTML={{ __html: currentTopic.content }}
          />
        </div>
      </div>
    </>
  );
}

export default Topics;